import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { currentUserData, companyData, type UserData, type CompanyData } from '../../data/scorecardData';
import { config } from '../../config/environment';
import managementService from '../../services/managementService';
import TenantSelector from '../common/TenantSelector';
import { NotificationBell } from '../notifications/NotificationBell';
import { COVERAGE_TAB_LABEL } from '../../constants/curriculumCoverage';

const ScorecardNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { tenantName } = useTenant();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScorecardsOpen, setIsScorecardsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scorecardsRef = useRef<HTMLDivElement>(null);
  const [userLocationName, setUserLocationName] = useState<string | null>(null);

  // Get user initials from auth context or fallback to centralized data
  const userInitials = React.useMemo(() => {
    if (user) {
      // Use management user data if available (regardless of environment)
      if (user.managementUser) {
        return managementService.getInitials(user.managementUser.first_name, user.managementUser.last_name);
      }
      // For other environments or when no management user data, use username
      return user.username.substring(0, 2).toUpperCase();
    }
    return currentUserData.initials;
  }, [user]);

  // Get user's first name for welcome message
  const userFirstName = React.useMemo(() => {
    if (user) {
      // Use management user data if available (regardless of environment)
      if (user.managementUser) {
        return user.managementUser.first_name;
      }
      // For other environments, extract first name from username/email
      const name = user.username || user.email || '';
      return name.split('@')[0].split('.')[0].split('+')[0];
    }
    return 'User';
  }, [user]);

  // Get user's last name for welcome message
  const userLastName = React.useMemo(() => {
    if (user) {
      return user.managementUser?.last_name || '';
    }
    return '';
  }, [user]);

  // Get user's role for display
  const userRole = React.useMemo(() => {
    if (user && user.managementUser) {
      return user.managementUser.role;
    }
    return null;
  }, [user]);

  // Fetch location name for manager users
  useEffect(() => {
    const fetchUserLocation = async () => {
      console.log('ScorecardNavigation: fetchUserLocation called');
      console.log('ScorecardNavigation: config.useDummyData =', config.useDummyData);
      console.log('ScorecardNavigation: user?.managementUser =', user?.managementUser);
      console.log('ScorecardNavigation: user role =', user?.managementUser?.role);
      console.log('ScorecardNavigation: user location_id =', user?.managementUser?.location_id);
      console.log('ScorecardNavigation: user tenant_id =', user?.managementUser?.tenant_id);
      
      if (!config.useDummyData && user?.managementUser?.role === 'Manager' && user?.managementUser?.location_id && user?.managementUser?.tenant_id) {
        try {
          console.log('ScorecardNavigation: Fetching locations for tenant:', user.managementUser.tenant_id);
          // For manager users, fetch locations by their tenant
          const response = await managementService.getLocationsByTenant(user.managementUser.tenant_id);
          console.log('ScorecardNavigation: Locations response:', response);
          if (response.success && response.locations) {
            const location = response.locations.find((loc: any) => loc.id === user?.managementUser?.location_id);
            console.log('ScorecardNavigation: Found location:', location);
            if (location) {
              console.log('ScorecardNavigation: Setting location name:', location.name);
              setUserLocationName(location.name);
            } else {
              console.log('ScorecardNavigation: Location not found for location_id:', user?.managementUser?.location_id);
            }
          } else {
            console.log('ScorecardNavigation: Failed to fetch locations:', response.error);
          }
        } catch (error) {
          console.error('ScorecardNavigation: Error fetching user location:', error);
        }
      } else {
        console.log('ScorecardNavigation: Conditions not met for fetching location');
      }
    };

    fetchUserLocation();
  }, [user?.managementUser]);
  
  // Get company and store data from centralized data
  const companyName = tenantName || companyData.name;
  const stateAbbrev = companyData.stateAbbrev;
  const storeLocations = companyData.storeLocations;
  
  // Get store from location state, localStorage, or default to Fremont
  const [selectedStore, setSelectedStore] = useState(() => {
    const state = location.state as any;
    const storedStore = localStorage.getItem('selectedStore');
    return state?.storeData?.storeName || storedStore || 'Fremont';
  });

  // Update selected store when location state changes or store is selected
  useEffect(() => {
    const state = location.state as any;
    if (state?.storeData?.storeName) {
      setSelectedStore(state.storeData.storeName);
      localStorage.setItem('selectedStore', state.storeData.storeName);
    }
  }, [location.state]);

  // Save selected store to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedStore', selectedStore);
  }, [selectedStore]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (scorecardsRef.current && !scorecardsRef.current.contains(event.target as Node)) {
        setIsScorecardsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Admin-only: Bespoke staff get the "Bespoke Admin" item in the avatar dropdown
  // (feat-125). isAdmin (role === 'Admin'), NOT the broader isStaff — the page is
  // <AdminRoute>-gated.
  const isAdmin = React.useMemo(() => {
    const raw = user?.managementUser?.role;
    if (!raw) return false;
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() === 'Admin';
  }, [user?.managementUser?.role]);

  // feat-191-evaluator-role (C3). A PRESENTATION memo only, mirroring isAdmin
  // (above) and isStaff (below) exactly — same Title-case normalization, same
  // local scope. Nothing here is an access control: the evaluator's boundary is
  // EvaluatorRoute/StaffRoute/AdminRoute, and createPermissionChecker already
  // returning false on all 12 capabilities. Neither of those files knows the word
  // "evaluator" and neither should.
  const isEvaluator = React.useMemo(() => {
    const raw = user?.managementUser?.role;
    if (!raw) return false;
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() === 'Evaluator';
  }, [user?.managementUser?.role]);

  // feat-191 C3 (design-lead Variant B, APPROVED). The evaluator's two openable
  // destinations. This is a SUBSTITUTION, not a deletion: an evaluator is not a
  // user with zero destinations — the queue dashboard and /disagreements are both
  // theirs — so the array changes rather than disappears, and every link still
  // satisfies AC6 ("no link to any page they cannot open").
  // /review/:interactionType gets no tab: it needs a type param, so it is a
  // drill-in from the dashboard table and a tab could only guess at the type.
  // Label is "Queue", not "Evaluations" — line 1 of the left cluster already
  // reads "Interaction Evaluations" a few pixels away.
  const EVALUATOR_TABS = [
    { label: 'Queue', path: '/restaurant/internal/interaction-evaluations' },
    { label: 'Disagreements', path: '/restaurant/internal/interaction-evaluations/disagreements' },
  ];

  // Top-level tabs (left cluster). Pilot Dashboard + Training Progress route to
  // "Coming soon" placeholders (real content lands feat-053b / feat-053c).
  const STAFF_TABS = [
    { label: 'Pilot Dashboard', path: '/restaurant/pilot-dashboard' },
    { label: 'Live Servers', path: '/restaurant/live-servers' },
    { label: 'Training Progress', path: '/restaurant/training-progress' },
    // feat-241 (P1/P8): coverage gets its OWN tab, as a sibling of Training
    // Progress rather than a sub-tab inside it. Always visible to staff — same
    // gating as its siblings, no tenant predicate: a tenant with only an empty
    // default track sees one column of `open` cells, which is truthful rather
    // than an error state, and it adds no derived-visibility rule that can drift
    // from what the phone derives. EVALUATOR_TABS is deliberately untouched — an
    // evaluator sees neither this tab nor the route.
    { label: COVERAGE_TAB_LABEL, path: '/restaurant/curriculum-coverage' },
    // feat-057: hidden, likely returns — see CHARTER. Route + UpsellDashboard component stay intact.
    // { label: 'Upsell Dashboard', path: '/restaurant/upsell-dashboard' },
    // feat-125: the admin "Bespoke Admin" entry moved to the avatar dropdown (below).
  ];

  const topTabs = isEvaluator ? EVALUATOR_TABS : STAFF_TABS;

  // Scorecards dropdown (right of the tabs). Leaderboard kept here per
  // DECISIONS §2. Mobile flattens these into the horizontal-scroll row, so this
  // one substitution covers desktop and mobile together — there is no separate
  // mobile branch. Empty for an evaluator: every scorecard is StaffRoute-gated.
  const scorecardItems = isEvaluator
    ? []
    : [
    // { label: 'Greeter Scorecard', path: '/restaurant/greeter-scorecard' },
    // { label: 'Cashier Scorecard', path: '/restaurant/cashier-scorecard' },
    { label: 'Server Scorecard', path: '/restaurant/server-scorecard' },
    { label: 'Manager Scorecard', path: '/restaurant/manager-scorecard' },
    { label: 'Store Scorecard', path: '/restaurant/store-scorecard' },
    { label: 'Leaderboard', path: '/restaurant/leaderboard' },
  ];

  const isActive = (path: string) => {
    // Direct match
    if (location.pathname === path) return true;
    
    // Performance history pages should show their parent scorecard as active
    const performanceHistoryMap: { [key: string]: string } = {
      '/restaurant/server-performance-history': '/restaurant/server-scorecard',
      '/restaurant/cashier-performance-history': '/restaurant/cashier-scorecard',
      '/restaurant/greeter-performance-history': '/restaurant/greeter-scorecard',
      '/restaurant/greeter-greeting-performance-history': '/restaurant/greeter-scorecard',
      '/restaurant/manager-shift-details': '/restaurant/manager-scorecard',
      '/restaurant/store-shift-details': '/restaurant/store-scorecard'
    };
    
    const parentScorecard = performanceHistoryMap[location.pathname];
    return parentScorecard === path;
  };

  // Scorecards dropdown trigger is "active" when the current route is any of
  // its items (including the performance-history sub-routes mapped above).
  const isScorecardsActive = scorecardItems.some((item) => isActive(item.path));

  // Navigation handler that preserves store selection
  const handleScorecardNavigation = (path: string) => {
    setIsScorecardsOpen(false);
    if (path === '/restaurant/store-scorecard' || path === '/restaurant/leaderboard') {
      // For store scorecard and leaderboard, pass store data
      navigate(path, {
        state: {
          storeData: {
            storeName: selectedStore
          }
        }
      });
    } else {
      // For other scorecards, just navigate normally
      navigate(path);
    }
  };

  const handleUserMenuClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };


  const handleBespokeAdmin = () => {
    setIsDropdownOpen(false);
    navigate('/restaurant/internal');
  };

  const handleAdministration = () => {
    setIsDropdownOpen(false);
    navigate('/restaurant/administration');
  };

  const handleSendNotification = () => {
    setIsDropdownOpen(false);
    navigate('/restaurant/administration/notifications');
  };

  const handleSignOut = async () => {
    setIsDropdownOpen(false);
    await logout();
    navigate('/scorecard-login', { replace: true });
  };

  // Staff = Admin + Manager + Corporate. Used to gate features that
  // tenant staff can use, not just Bespoke admins. Corporate is treated
  // identically to Manager for access decisions.
  const isStaff = React.useMemo(() => {
    const raw = user?.managementUser?.role;
    if (!raw) return false;
    const normalized = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    return normalized === 'Admin' || normalized === 'Manager' || normalized === 'Corporate';
  }, [user?.managementUser?.role]);

  const fullName = `${userFirstName.charAt(0).toUpperCase() + userFirstName.slice(1)} ${userLastName.charAt(0).toUpperCase() + userLastName.slice(1)}`.trim();

  return (
    <div data-app-nav className="bg-white border-b border-softBorder shadow-sm sticky top-0 z-50">
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-3">
        <div className="flex items-center justify-between gap-6">
          {/* Left side - Company, Store, and Welcome message */}
          <div className="flex items-center gap-6 min-w-0">
            <div className="min-w-0">
              <div className="text-base font-semibold text-ink">
                {isEvaluator ? (
                  // feat-191 C3 — line 1 of the re-tasked left cluster. STATIC TEXT,
                  // not a control: the tenant selector is meaningless to a grader
                  // whose queue spans every customer, and rendering it as a button
                  // would advertise a picker that does nothing. TenantSelector is
                  // simply not mounted here, so its /api/tenant fetch cannot fire.
                  <span className="font-display text-base font-semibold">
                    <span className="text-ink">Bespoke</span>
                    <span className="text-inkMuted"> · </span>
                    <span className="text-coral">Interaction Evaluations</span>
                  </span>
                ) : (
                  <TenantSelector variant="navigation" />
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap mt-0.5">
                {/* feat-191: capped. This row is flex-wrap, so an uncapped long
                    name pushes the badges to a third line and grows the nav —
                    and nav height is load-bearing for InteractionTypeReview's
                    sticky toolbar offset. */}
                <div className="text-xs text-inkMuted truncate max-w-[260px]" title={`Welcome, ${fullName}`}>
                  Welcome, {fullName}
                </div>
                {userRole && (
                  <div className={`inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full ${
                    // feat-191: an evaluator fell through this chain to the gray
                    // "unknown role" branch and rendered the raw lowercase string
                    // `evaluator`, which reads as broken. Keyed on the NORMALIZED
                    // memo rather than adding a raw-string comparison, and placed
                    // first so no existing role's branch changes (AC8/AC9).
                    isEvaluator
                      ? 'bg-teal-100 text-teal-800'
                      : userRole === 'Admin'
                      ? 'bg-purple-100 text-purple-800'
                      : userRole === 'Manager'
                      ? 'bg-blue-100 text-blue-800'
                      : userRole === 'Corporate'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {isEvaluator ? 'Evaluator' : userRole === 'Admin' ? 'Admin' : userRole === 'Manager' ? 'Store Manager' : userRole === 'Corporate' ? 'Corporate Manager' : userRole}
                  </div>
                )}
                {/* feat-191: the one fact an evaluator cannot learn anywhere else
                    on the screen — their queue spans every customer (D2). Drops at
                    mobile width rather than wrapping: it is orientation, not a control. */}
                {isEvaluator && (
                  <div className="hidden sm:inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full bg-cream-dark text-inkMuted">
                    All tenants
                  </div>
                )}
                {userRole === 'Manager' && userLocationName && (
                  <div className="inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full bg-gray-100 text-gray-700">
                    {userLocationName}
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Links - Hidden on mobile */}
            <nav className="hidden lg:flex items-center gap-1">
              {topTabs.map((page) => {
                const active = isActive(page.path);
                return (
                  <button
                    key={page.path}
                    onClick={() => handleScorecardNavigation(page.path)}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      active
                        ? 'bg-navActiveBg text-navActive font-semibold'
                        : 'text-ink hover:bg-cream-dark'
                    }`}
                  >
                    {page.label}
                  </button>
                );
              })}

              {/* Scorecards dropdown — Server / Manager / Store / Leaderboard.
                  feat-191: derived from the data rather than from a second role
                  predicate — scorecardItems is empty for an evaluator, and a
                  trigger for an empty menu is just a dead button. */}
              {scorecardItems.length > 0 && (
              <div className="relative" ref={scorecardsRef}>
                <button
                  onClick={() => setIsScorecardsOpen((open) => !open)}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-1 ${
                    isScorecardsActive
                      ? 'bg-navActiveBg text-navActive font-semibold'
                      : 'text-ink hover:bg-cream-dark'
                  }`}
                  aria-haspopup="true"
                  aria-expanded={isScorecardsOpen}
                >
                  Scorecards
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {isScorecardsOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-softBorder min-w-[210px] py-2 z-50">
                    {scorecardItems.map((item) => {
                      const active = isActive(item.path);
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleScorecardNavigation(item.path)}
                          className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                            active
                              ? 'bg-navActiveBg text-navActive font-semibold'
                              : 'text-ink hover:bg-cream-dark'
                          }`}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              )}
            </nav>
          </div>

          {/* Right side - Notification Bell + User Profile Button */}
          <div className="flex items-center gap-2">
            {/* Bell is suppressed on /restaurant_admin per plan §12.1.
                feat-191: also hidden for evaluators (design-lead ruling) — they are
                not on a location or a tenant staff feed, so the bell is noise.
                PRESENTATION ONLY, NOT an access control: /restaurant/notifications
                is gated by <ProtectedRoute> (App.tsx:316 — authenticated, not
                StaffRoute), so an evaluator can still reach that page by URL.
                Hiding the bell removes the nav path and nothing else. Do not
                restate this as "evaluators can't reach notifications" — that is
                false, and the gate question is open with the architect. */}
            {!isEvaluator && !location.pathname.startsWith('/restaurant_admin') && <NotificationBell />}
          <div className="relative flex-shrink-0" ref={dropdownRef}>
            <button
              onClick={handleUserMenuClick}
              className="w-9 h-9 rounded-full bg-gradient-to-br from-coral to-gold text-white text-sm font-semibold flex items-center justify-center hover:opacity-90 transition-opacity"
            >
              {userInitials}
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-softBorder z-50">
                <div className="py-1">
                  {isEvaluator ? (
                    // feat-191 C3 (design-lead ruling) — the evaluator menu is an
                    // ALLOWLIST OF ONE, written as an enumeration of what they DO
                    // get rather than a subtraction of what they don't. That is the
                    // better fix, not merely an equivalent one: "Tenant Management"
                    // used to render below with NO role condition at all, outside
                    // both the isAdmin and isStaff blocks, so every role got it and
                    // AC6 ("no link to any page they cannot open") failed on it.
                    // (feat-318-admin-ui-conveniences later moved it off this menu.)
                    // Subtracting would leave the NEXT item someone adds inheriting
                    // visibility by default — which is exactly how that gap appeared.
                    // Adding anything for an evaluator must be a deliberate edit here.
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 text-sm text-coral hover:bg-cream-dark transition-colors"
                    >
                      Sign out
                    </button>
                  ) : (
                  <>
                  {isAdmin && (
                    <button
                      onClick={handleBespokeAdmin}
                      className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-cream-dark transition-colors"
                    >
                      Bespoke Admin
                    </button>
                  )}
                  {isStaff && (
                    <button
                      onClick={handleAdministration}
                      className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-cream-dark transition-colors"
                    >
                      Administration
                    </button>
                  )}
                  {isStaff && (
                    <button
                      onClick={handleSendNotification}
                      className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-cream-dark transition-colors"
                    >
                      Send a Notification
                    </button>
                  )}
                  {/* feat-318-admin-ui-conveniences: "Tenant Management" moved off this
                      menu (for every role) onto a tile on the Bespoke Admin page
                      (InternalPage.tsx). The /tenant_management routes are unchanged. */}
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left px-4 py-2 text-sm text-coral hover:bg-cream-dark transition-colors"
                  >
                    Sign out
                  </button>
                  </>
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Mobile Navigation - horizontal scroll (top tabs + scorecard items flattened) */}
        <nav className="lg:hidden flex gap-2 mt-3 overflow-x-auto pb-2">
          {[...topTabs, ...scorecardItems].map((page) => {
            const active = isActive(page.path);
            return (
              <button
                key={page.path}
                onClick={() => handleScorecardNavigation(page.path)}
                className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-navActiveBg text-navActive font-semibold'
                    : 'text-ink hover:bg-cream-dark'
                }`}
              >
                {page.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default ScorecardNavigation;
